import { supabase, isSupabaseConfigured } from '../../lib/supabase'

const navItems = [
  { path: '/admin/dashboard',     label: 'Dashboard',       icon: 'ri-dashboard-line' },
  { path: '/admin/products',      label: 'Proizvodi',       icon: 'ri-box-3-line' },
  { path: '/admin/blog',          label: 'Blog',            icon: 'ri-article-line' },
  { path: '/admin/homepage',      label: 'Početna',         icon: 'ri-home-4-line' },
  { path: '/admin/faqs',          label: 'FAQ',             icon: 'ri-question-answer-line' },
  { path: '/admin/testimonials',  label: 'Testimonijali',   icon: 'ri-user-star-line' },
  { path: '/admin/settings',      label: 'Podešavanja',     icon: 'ri-settings-3-line' },
]

export default function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [checking, setChecking] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    if (!isSupabaseConfigured) {
      navigate('/admin/login')
      return
    }
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate('/admin/login')
      else setChecking(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') navigate('/admin/login')
    })
    return () => subscription.unsubscribe()
  }, [])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/admin/login')
  }

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400 flex items-center gap-3">
          <i className="ri-loader-4-line text-2xl animate-spin" />
          <span>Provjera sesije...</span>
        </div>
      </div>
    )
  }

  const currentLabel = navItems.find(n => location.pathname.startsWith(n.path))?.label ?? 'Admin'

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30
        w-64 bg-slate-900 flex flex-col
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-fill text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-tight">SafeGuard CMS</p>
              <p className="text-slate-500 text-xs">Admin panel</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            const active = location.pathname.startsWith(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition
                  ${active
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }
                `}
              >
                <i className={`${item.icon} text-base`} />
                {item.label}
              </Link>
            )
          })}
        </nav>

        {/* Bottom: logout */}
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-red-400 transition w-full"
          >
            <i className="ri-logout-box-line text-base" />
            Odjavi se
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4 flex items-center gap-4">
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="ri-menu-line text-xl" />
          </button>
          <h1 className="text-gray-900 font-semibold">{currentLabel}</h1>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              target="_blank"
              className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1.5 transition"
            >
              <i className="ri-external-link-line" />
              Pogledaj sajt
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
